import React, { useState, useEffect } from "react";
import { ErrorResponse } from "../../../Models/Error.js";
import { Shipment } from "../../../Models/Shipment.js";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  callSuccessToast,
  callErrorToast,
} from "../../../utilities/utilities.ts";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { useNavigate } from "react-router-dom";
import {
  ShipmentDeleteContextValue,
  ShipmentDeleteProviderProps,
} from "./types.ts";
import { ShipmentDeleteContext } from "./contexts.ts";

const shipmentsService = new ShipmentsService();

export const ShipmentDeleteProvider: React.FC<ShipmentDeleteProviderProps> = ({
  children,
  data = [],
  callback,
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorResponse<Shipment> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const deleteShipments = async (): Promise<void> => {
    setLoader(true);

    try {
      // Execute all deletion requests concurrently
      const results = await Promise.allSettled(
        data.map((id) => shipmentsService.delete(id)),
      );

      // Separate results into successful and rejected categories
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      // If at least one deletion failed
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Shipment>;
        setError(firstError);

        // If some deletions succeeded, trigger the callback to refresh UI/state
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // If all deletions were successful
      if (callback) callback();
      navigate("/shipments");
    } catch (unexpected) {
      // Generic error fallback for unexpected exceptions
      const err: ErrorResponse<Shipment> = {
        response: {
          data: {},
        },
      };

      setError(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  const contextValue: ShipmentDeleteContextValue = {
    data,
    loader,
    deleteShipments,
    callback,
  };

  return (
    <ShipmentDeleteContext.Provider value={contextValue}>
      {children}
    </ShipmentDeleteContext.Provider>
  );
};
